// A call ringing (Stage 4.8f.4a, mockup "Incoming"): who it is as the CRM knows them and the assistant's hand-off when it passed them
// on (RingBriefing, 4.5); decline or answer. Also a desktop notification when the tab is behind (usePhoneLine). A light card — the
// app's sidebar is the only dark surface.
import { Box, ButtonBase, Dialog, Typography } from "@mui/material";
import { Phone, PhoneOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import RingBriefing from "../pages/handoff/RingBriefing";
import { Initial } from "./phoneUi";
import { useSince, fmtDur } from "./phoneFormat";

export default function PhoneIncoming({ call, name, address, briefing, handoff, onAnswer, onDecline, micBlocked }) {
  const { t } = useTranslation("phone");
  const ringing = useSince(call?.since);
  if (!call) return null;
  const shown = name || call.name || call.number || t("unknown");
  return (
    <Dialog open maxWidth="xs" fullWidth disableEscapeKeyDown aria-labelledby="phone-incoming-name"
      PaperProps={{ sx: { borderRadius: "var(--r-xl)", background: "var(--surface-card)", border: "1px solid var(--line-100)", boxShadow: "var(--shadow-lg)" } }}>
      <Box sx={{ p: "24px 24px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", textAlign: "center" }}>
        <Typography sx={{ font: "700 12px/1 var(--font-ui)", letterSpacing: ".8px", textTransform: "uppercase", color: "var(--ink-500)" }}>
          {t("incomingCall")} · {fmtDur(ringing)}
        </Typography>
        <Box sx={{ position: "relative", my: "8px", display: "grid", placeItems: "center" }}>
          <Box aria-hidden="true" sx={{ position: "absolute", width: 84, height: 84, borderRadius: "50%", border: "2px solid var(--success)",
            "@media (prefers-reduced-motion: no-preference)": { animation: "phoneRing 1.8s var(--ease-out) infinite" },
            "@keyframes phoneRing": { "0%": { transform: "scale(.8)", opacity: 0.8 }, "100%": { transform: "scale(1.5)", opacity: 0 } } }} />
          <Initial name={shown} size={64} />
        </Box>
        <Typography id="phone-incoming-name" dir="auto" sx={{ font: "800 22px/1.2 var(--font-ui)", fontFamily: "var(--font-ui), var(--font-ar)", color: "var(--ink-900)" }}>{shown}</Typography>
        {shown !== call.number && call.number && <Typography sx={{ font: "600 14px/1.2 var(--font-ui)", color: "var(--ink-600)" }}><span dir="ltr">{call.number}</span></Typography>}
        {address && <Typography sx={{ font: "500 12px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{address}</Typography>}
        <Box sx={{ alignSelf: "stretch" }}><RingBriefing briefing={briefing} handoff={handoff} light /></Box>
        {micBlocked && <Typography role="alert" sx={{ font: "600 12px/1.4 var(--font-ui)", color: "var(--danger)" }}>{t("micBlockedShort")}</Typography>}
        <Box sx={{ display: "flex", gap: "12px", alignSelf: "stretch", mt: "12px" }}>
          <ButtonBase onClick={() => onDecline(call.id)} sx={{ flex: 1, minHeight: 48, gap: "8px", borderRadius: "12px", background: "var(--danger-bg)", color: "var(--danger)", font: "700 14px/1 var(--font-ui)" }}>
            <PhoneOff size={18} aria-hidden="true" />{t("decline")}
          </ButtonBase>
          <ButtonBase autoFocus onClick={() => onAnswer(call.id)} sx={{ flex: 1, minHeight: 48, gap: "8px", borderRadius: "12px", background: "var(--success)", color: "#fff", font: "700 14px/1 var(--font-ui)",
            "&:focus-visible": { outline: "2px solid var(--brand-violet)", outlineOffset: 2 } }}>
            <Phone size={18} aria-hidden="true" />{t("answer")}
          </ButtonBase>
        </Box>
      </Box>
    </Dialog>
  );
}
